import React from "react";
import { createPortal } from "react-dom";
import { initials } from "@/lib/util";

export const Kicker = ({ children, className = "" }) => (
  <div className={`kicker ${className}`}>{children}</div>
);

export const Rule = ({ className = "" }) => <div className={`double-rule ${className}`} />;

export const Chip = ({ children, color, testId, tint = true }) => (
  <span
    className="chip"
    data-testid={testId}
    style={
      color
        ? {
            color,
            borderColor: `${color}55`,
            background: tint ? `${color}14` : undefined,
          }
        : undefined
    }
  >
    {children}
  </span>
);

export const Avatar = ({ name, color = "#1E3A8A", size = 36, dim = false, testId }) => (
  <span
    data-testid={testId}
    title={name}
    className="serif inline-flex items-center justify-center rounded-full shrink-0 select-none"
    style={{
      width: size,
      height: size,
      background: color,
      color: "#FFFFFF",
      fontSize: Math.round(size * 0.4),
      fontWeight: 500,
      letterSpacing: "0.02em",
      opacity: dim ? 0.26 : 1,
      boxShadow: dim
        ? "none"
        : "inset 0 1px 0 rgba(255,255,255,.22), 0 0 0 1px rgba(37,99,235,.30), 0 6px 14px -10px rgba(15,23,42,.35)",
      transition: "opacity .25s var(--ease), box-shadow .25s var(--ease)",
    }}
  >
    {initials(name)}
  </span>
);

export const Card = ({ children, className = "", lift = false, ...rest }) => (
  <div className={`card-t p-6 sm:p-7 ${lift ? "card-lift" : ""} ${className}`} {...rest}>
    {children}
  </div>
);

export const PageHead = ({ kicker, title, sub, right }) => (
  <header className="mb-7">
    <div className="kicker mb-2">{kicker}</div>
    <div className="flex items-end justify-between gap-6 flex-wrap">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
      {right}
    </div>
    {sub && <p className="lede mt-2.5 max-w-2xl">{sub}</p>}
    <div className="hair mt-5" />
  </header>
);

export const SectionHead = ({ children, right }) => (
  <div className="flex items-end justify-between gap-4 mb-4">
    <div className="eyebrow-rule flex-1">
      <Kicker>{children}</Kicker>
    </div>
    {right}
  </div>
);

export const Empty = ({ line, testId }) => (
  <div data-testid={testId} className="card-t px-8 py-12 text-center">
    <p className="text-[14px]" style={{ color: "var(--ink-mute)" }}>
      {line}
    </p>
  </div>
);

export const Bar = ({ value, color = "var(--brass-bright)" }) => (
  <div
    className="h-[7px] rounded-full overflow-hidden"
    style={{
      background: "rgba(15,23,42,.08)",
      boxShadow: "inset 0 1px 2px rgba(15,23,42,.10)",
    }}
  >
    <div
      className="h-full rounded-full"
      style={{
        width: `${Math.max(0, Math.min(100, value || 0))}%`,
        background: `linear-gradient(90deg, ${color}cc, ${color})`,
        transition: "width .6s var(--ease)",
      }}
    />
  </div>
);

export const Segmented = ({ options, value, onChange, testId }) => (
  <div className="inline-flex flex-wrap gap-1 p-1 inset" data-testid={testId}>
    {options.map((o) => {
      const v = typeof o === "object" ? o.value : o;
      const label = typeof o === "object" ? o.label : o;
      const active = String(v) === String(value);
      return (
        <button
          key={String(v)}
          type="button"
          data-testid={testId ? `${testId}-${v}` : undefined}
          onClick={() => onChange(v)}
          className="min-w-[36px] px-2.5 py-1.5 text-[13px] rounded-md"
          style={{
            background: active ? "var(--brass-bright)" : "transparent",
            color: active ? "#fff" : "var(--ink-mute)",
            fontWeight: active ? 500 : 400,
            transition: "background-color .2s var(--ease), color .2s var(--ease)",
          }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

export const Field = ({ label, ...rest }) => (
  <label className="block">
    {label && <span className="kicker block mb-2">{label}</span>}
    <input className="field" {...rest} />
  </label>
);

export const Area = ({ label, ...rest }) => (
  <label className="block">
    {label && <span className="kicker block mb-2">{label}</span>}
    <textarea className="field" rows={3} {...rest} />
  </label>
);

export const Pick = ({ label, options, ...rest }) => (
  <label className="block">
    {label && <span className="kicker block mb-2">{label}</span>}
    <select className="field" {...rest}>
      {options.map((o) => {
        const v = typeof o === "object" ? o.value : o;
        const l = typeof o === "object" ? o.label : o;
        return (
          <option key={String(v)} value={v}>
            {l}
          </option>
        );
      })}
    </select>
  </label>
);

export const Sparkline = ({ points = [], width = 120, height = 30 }) => {
  if (points.length < 2)
    return (
      <span className="text-xs italic serif" style={{ color: "var(--ink-mute)" }}>
        Not enough data yet
      </span>
    );
  const max = 10;
  const step = width / (points.length - 1);
  const coords = points.map((p, i) => [i * step, height - (p / max) * height]);
  const d = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const area = `${d} L${width},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} aria-hidden className="overflow-visible">
      <path d={area} fill="rgba(37,99,235,.10)" />
      <path d={d} fill="none" stroke="var(--brass-bright)" strokeWidth="1.6" strokeLinecap="round" />
      {coords.slice(-1).map(([x, y]) => (
        <circle key="last" cx={x} cy={y} r="2.6" fill="var(--brass)" />
      ))}
    </svg>
  );
};

export const Modal = ({ open, onClose, title, children, testId }) => {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ background: "rgba(15,23,42,.5)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        data-testid={testId}
        className="card-t w-full sm:max-w-lg p-7 sm:p-8 fade-in max-h-[92vh] overflow-y-auto rounded-b-none sm:rounded-b-[12px]"
        onClick={(e) => e.stopPropagation()}
      >
        <Kicker>{title}</Kicker>
        <div className="hair mt-3 mb-5" />
        <div className="space-y-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export const Stat = ({ label, value, sub, testId }) => (
  <div className="px-5 py-5 sm:px-7 sm:py-6 flex-1 min-w-0" data-testid={testId}>
    <Kicker className="!text-[9px]">{label}</Kicker>
    <div className="numeral text-3xl sm:text-4xl mt-2.5 leading-none">{value}</div>
    {sub && (
      <div className="text-xs mt-2" style={{ color: "var(--ink-mute)" }}>
        {sub}
      </div>
    )}
  </div>
);

export const StatStrip = ({ children }) => (
  <div className="card-t overflow-hidden flex flex-col sm:flex-row divide-hair sm:divide-y-0">
    <style>{`@media (min-width:640px){.stat-strip-sep>*+*{border-top:0;border-left:1px solid var(--hairline-soft)}}`}</style>
    <div className="flex flex-col sm:flex-row w-full stat-strip-sep divide-hair">{children}</div>
  </div>
);
